import React, { ReactNode } from 'react';
import _startCase from 'lodash/startCase';
import cx from 'classnames';

import { pickColorFromRoleType } from '@trqk/utils';

import { AvatarHalo, LoadingEllipses } from '..';

interface Props {
    height: number;
    highlightOnHover?: boolean;
    imageUrl?: string;
    avatarLabel?: ReactNode;
    loading?: boolean;
    name?: string;
    pro?: string;
    roleType: ProRoleType | null;
    topBorder?: boolean;
    width: number;
}

/**
 * Displays a card to be used in a dropdown list of available entities.
 */
export const EntityCard = ({
    height,
    highlightOnHover = false,
    imageUrl,
    loading = false,
    name,
    avatarLabel,
    pro,
    roleType,
    topBorder = false,
    width,
}: Props): JSX.Element => {
    const avatarColor = pickColorFromRoleType(roleType);
    const roleInitial =
        roleType && roleType[0] ? roleType[0].toUpperCase() : '';

    return (
        <div
            className={cx({
                'hover:bg-highlight-fadedpurple': highlightOnHover,
            })}
            data-cy="entity-card"
            style={{ height, width }}
        >
            {loading && (
                <div className="flex items-center justify-center w-full h-full">
                    <LoadingEllipses fillColor="secondary-400" width={50} />
                </div>
            )}
            {!loading && (
                <div
                    className={cx(
                        'flex justify-start items-center px-2 pt-2 w-full',
                        {
                            'border-t border-tertiary-200': topBorder,
                        },
                    )}
                    style={{ width }}
                >
                    <div className="flex-none">
                        <AvatarHalo
                            color={avatarColor}
                            imageUrl={imageUrl}
                            name={name}
                            label={avatarLabel}
                        />
                    </div>
                    <div className="flex flex-col w-full min-w-0 ml-4">
                        <div className="flex-auto text-sm font-semibold tracking-wider truncate">
                            {_startCase(name)}
                        </div>
                        <div>
                            {pro && (
                                <span className="text-xs font-semibold tracking-widest text-tertiary-300">
                                    {pro}
                                </span>
                            )}
                            {roleType !== 'unknown' && roleType !== null && (
                                <>
                                    &nbsp;·&nbsp;
                                    <span
                                        className={`text-${avatarColor}-500 text-xs font-bold`}
                                    >
                                        {roleInitial}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
